/**
 * Mission Critical Items Configuration
 * Centralized definition of device status items with priority, severity, and status logic
 */

import { AndroidStatus, CameraConfig, CameraMode, GpsLocation, GpsError } from '@/types';
import { getGpsErrorMessage, getGpsErrorTagText } from '@/utils/gpsErrorMessages';

/**
 * Map battery percentage to battery_android_frame icon
 * frame_1 (≤16%) through frame_6 (≤99%), frame_full (100%)
 */
function getBatteryIcon(percent: number | null | undefined): string {
  if (percent === null || percent === undefined) return 'battery_android_frame_4';
  if (percent >= 100) return 'battery_android_frame_full';
  if (percent >= 84) return 'battery_android_frame_6';
  if (percent >= 67) return 'battery_android_frame_5';
  if (percent >= 50) return 'battery_android_frame_4';
  if (percent >= 34) return 'battery_android_frame_3';
  if (percent >= 17) return 'battery_android_frame_2';
  return 'battery_android_frame_1';
}

export interface MissionCriticalItemStatus {
  isIssue: boolean;
  icon: string;
  /** Custom text icon (rendered as bold text instead of Material icon) */
  textIcon?: string;
  /** Whether to render textIcon with strikethrough */
  textIconStrike?: boolean;
  tagText: string;
  description: string;
  /** Override item severity for this specific status (e.g., offline = critical, weak signal = potential) */
  severityOverride?: 'critical' | 'potential';
}

/** Context passed to getStatus for evaluating mission critical items */
export interface MissionCriticalContext {
  device: AndroidStatus['device'] | null | undefined;
  capturing: AndroidStatus['capturing'] | null | undefined;
  cameraMode: CameraMode;
  gpsLocation?: GpsLocation;
  gpsError?: GpsError;
  isOnline?: boolean;
}

export interface MissionCriticalItem {
  id: string;
  priority: number;
  label: string;
  severity: 'critical' | 'potential';
  getStatus: (context: MissionCriticalContext) => MissionCriticalItemStatus;
}

/**
 * Mission critical items ordered by priority
 */
export const missionCriticalItems: MissionCriticalItem[] = [
  {
    id: 'permissions',
    priority: 1,
    label: 'Permissions',
    severity: 'critical',
    getStatus: ({ device }) => {
      const perms = device?.permissions;

      // Fallback for old app versions that only send batteryOptimizationDisabled
      if (!perms) {
        const isDisabled = device?.batteryOptimizationDisabled ?? true;
        return {
          isIssue: !isDisabled,
          icon: isDisabled ? 'verified_user' : 'verified_user',
          tagText: isDisabled ? 'All granted' : 'Battery restricted',
          description: isDisabled
            ? 'Battery optimization is disabled (older app version — update for full permission monitoring).'
            : 'Android may kill the app to save battery. Open the app and tap Permissions to fix.',
          severityOverride: !isDisabled ? 'critical' : undefined,
        };
      }

      // Critical permissions: camera, notification, batteryOptimization
      const criticalMissing: string[] = [];
      if (!perms.camera) criticalMissing.push('Camera');
      if (!perms.notification) criticalMissing.push('Notifications');
      if (!perms.batteryOptimization) criticalMissing.push('Battery optimization');

      // Non-critical permissions: location, locationService, installPackages
      const warningMissing: string[] = [];
      if (!perms.location) warningMissing.push('Location');
      if (!perms.locationService) warningMissing.push('Location service');
      if (!perms.installPackages) warningMissing.push('Install updates');

      const allMissing = [...criticalMissing, ...warningMissing];

      if (allMissing.length === 0) {
        return {
          isIssue: false,
          icon: 'verified_user',
          tagText: 'All granted',
          description: 'All permissions are correctly granted.',
        };
      }

      return {
        isIssue: true,
        icon: 'verified_user',
        tagText: `${allMissing.length} missing`,
        description: `Missing: ${allMissing.join(', ')}. Open the app and tap Permissions to fix.`,
        severityOverride: criticalMissing.length > 0 ? 'critical' : 'potential',
      };
    },
  },
  {
    id: 'charging',
    priority: 2,
    label: 'Charging',
    severity: 'critical',
    getStatus: ({ device }) => {
      const isCharging = device?.batteryCharging ?? false;
      return {
        isIssue: !isCharging,
        icon: isCharging ? 'power' : 'power_off',
        tagText: isCharging ? 'Powered' : 'No power',
        description: isCharging
          ? 'Device is powered and will run indefinitely.'
          : 'Battery will drain and device will shut down. If battery drops below the survival threshold, the device will enter deep sleep.',
      };
    },
  },
  {
    id: 'batteryLevel',
    priority: 3,
    label: 'Battery',
    severity: 'potential',
    getStatus: ({ device }) => {
      const batteryLevel = device?.batteryLevel;
      const isLow = batteryLevel !== undefined && batteryLevel <= 20;
      return {
        isIssue: isLow,
        icon: getBatteryIcon(batteryLevel),
        tagText: batteryLevel !== undefined ? `${batteryLevel}%` : 'Unknown',
        description: isLow
          ? `Device battery is low at ${batteryLevel}%.`
          : batteryLevel !== undefined
            ? `Device battery at ${batteryLevel}%.`
            : 'Device battery level is unknown.',
      };
    },
  },
  {
    id: 'batteryTemperature',
    priority: 3.5,
    label: 'Temperature',
    severity: 'potential',
    getStatus: ({ device }) => {
      const current = device?.batteryTemperature;
      const max = device?.batteryTemperatureMax;
      const maxAt = device?.batteryTemperatureMaxAt;

      // No data available (older app version or invalid reading)
      if (current === undefined || current === null || current <= 0) {
        return {
          isIssue: false,
          icon: 'thermostat',
          tagText: 'Unknown',
          description: 'Battery temperature is not reported by this device.',
        };
      }

      // Severity is based on current temperature.
      // If a recent peak (≤2h ago) was higher, use that instead — it may still be cooling down.
      // Thresholds: <40°C OK, 40-45°C warning, ≥45°C critical.
      const hasRecentPeak = max !== undefined && max !== null && max > current && maxAt;
      const peakHoursAgo = hasRecentPeak
        ? (Date.now() - maxAt.toDate().getTime()) / (1000 * 60 * 60)
        : Infinity;
      const isRecentPeak = hasRecentPeak && peakHoursAgo <= 2;
      const effective = isRecentPeak ? Math.max(current, max!) : current;
      const isCritical = effective >= 45;
      const isWarning = !isCritical && effective >= 40;
      const isIssue = isCritical || isWarning;

      // Format the peak description if there is a meaningful peak in last 24h
      let peakInfo = '';
      if (hasRecentPeak) {
        const peakDate = maxAt.toDate();
        const peakTime = peakDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const roundedHours = Math.round(peakHoursAgo);
        const ago = roundedHours === 0 ? 'less than 1h ago' : roundedHours === 1 ? '1 hour ago' : `${roundedHours} hours ago`;
        peakInfo = ` Peak of ${max!.toFixed(1)}°C reached at ${peakTime} (${ago}).`;
      }

      let description: string;
      if (isCritical) {
        description = `Battery temperature: ${current.toFixed(1)}°C.${peakInfo} Device may have been exposed to direct sunlight or a heat source. Consider moving it to a cooler location.`;
      } else if (isWarning) {
        description = `Battery temperature: ${current.toFixed(1)}°C.${peakInfo} Device is warmer than normal. Monitor for further increases.`;
      } else if (peakInfo) {
        description = `Battery temperature: ${current.toFixed(1)}°C.${peakInfo} Temperature has since returned to normal.`;
      } else {
        description = `Battery temperature: ${current.toFixed(1)}°C. No high temperature peaks in the last 24 hours.`;
      }

      return {
        isIssue,
        icon: 'thermostat',
        tagText: `${current.toFixed(0)}°C`,
        description,
        severityOverride: isCritical ? 'critical' : undefined,
      };
    },
  },
  {
    id: 'dslrConnection',
    priority: 3,
    label: 'External Camera',
    severity: 'critical',
    getStatus: ({ device, cameraMode }) => {
      // Only show this item when in DSLR mode
      if (cameraMode !== 'dslr') {
        return {
          isIssue: false,
          icon: 'camera',
          tagText: 'Internal camera',
          description: 'Using internal Android camera for captures.',
        };
      }

      const dslr = device?.dslr;
      const isConnected = dslr?.connected ?? false;
      const modelInfo = isConnected ? `${dslr?.manufacturer} ${dslr?.model}`.trim() : '';

      return {
        isIssue: !isConnected,
        icon: isConnected ? 'photo_camera' : 'no_photography',
        tagText: isConnected ? modelInfo : 'Disconnected',
        description: isConnected
          ? `External camera is connected and ready for capture.`
          : dslr?.disconnectReason === 'usb_disconnected'
            ? 'USB cable disconnected. Captures are being skipped.'
            : 'External camera is not connected. Connect the camera via USB or switch to Internal mode.',
      };
    },
  },
  {
    id: 'networkConnection',
    priority: 5,
    label: 'Network',
    severity: 'potential',
    getStatus: ({ device }) => {
      const networkType = device?.networkType ?? 'unknown';
      const signalStrength = device?.signalStrength;
      const networkOperator = device?.networkOperator;

      const signalText = signalStrength !== undefined && signalStrength !== null ? ` ${signalStrength}%` : '';
      const isWifi = networkType === 'wifi';

      // Build network label: "WiFi", "WINDTRE 4G", "Mobile Data", etc.
      let networkLabel: string;
      let descriptionLabel: string;
      if (isWifi) {
        networkLabel = 'WiFi';
        descriptionLabel = 'WiFi';
      } else if (networkType === 'offline') {
        networkLabel = 'Offline';
        descriptionLabel = 'offline';
      } else {
        // Mobile data: show operator + generation if available
        const generation = ['2g', '3g', '4g', '5g'].includes(networkType) ? networkType.toUpperCase() : '';
        if (networkOperator && generation) {
          networkLabel = `${networkOperator} ${generation}`;
          descriptionLabel = `mobile data (${networkOperator} ${generation})`;
        } else if (networkOperator) {
          networkLabel = networkOperator;
          descriptionLabel = `mobile data (${networkOperator})`;
        } else if (generation) {
          networkLabel = `Mobile ${generation}`;
          descriptionLabel = `mobile data (${generation})`;
        } else {
          networkLabel = 'Mobile Data';
          descriptionLabel = 'mobile data';
        }
      }

      // < 25% = Critical (red)
      if (signalStrength !== undefined && signalStrength !== null && signalStrength < 25) {
        return {
          isIssue: true,
          severityOverride: 'critical',
          icon: isWifi ? 'wifi_1_bar' : 'signal_cellular_alt_1_bar',
          tagText: `${networkLabel}${signalText}`,
          description: `Network signal is very weak at ${signalStrength}%. Uploads may fail. Move closer to the router or improve cellular reception.`,
        };
      }

      // 25-49% = Warning (yellow)
      if (signalStrength !== undefined && signalStrength !== null && signalStrength < 50) {
        return {
          isIssue: true,
          severityOverride: 'potential',
          icon: isWifi ? 'wifi_2_bar' : 'signal_cellular_alt_2_bar',
          tagText: `${networkLabel}${signalText}`,
          description: `Network signal is moderate at ${signalStrength}%. Uploads may be slow.`,
        };
      }

      // >= 50% = OK (green)
      return {
        isIssue: false,
        icon: isWifi ? 'wifi' : 'signal_cellular_alt',
        tagText: `${networkLabel}${signalText}`,
        description: `Device is connected via ${descriptionLabel}.`,
      };
    },
  },
  {
    id: 'gpsPosition',
    priority: 6,
    label: 'GPS',
    severity: 'potential',
    getStatus: ({ gpsLocation, gpsError, isOnline }) => {
      const hasPosition = !!gpsLocation;

      // Helper: check if a gpsError refers to the same request as the current
      // location fix (Android writes BOTH gpsLocation + gpsError.code='timeout'
      // when it returns a low-accuracy fix instead of timing out completely).
      const locationMs = gpsLocation?.timestamp?.toMillis?.() ?? 0;
      const errorMs = gpsError?.timestamp?.toMillis?.() ?? 0;
      const isLowAccuracyFix =
        hasPosition &&
        !!gpsError &&
        gpsError.code === 'timeout' &&
        Math.abs(errorMs - locationMs) < 5000;
      const errorIsNewerThanLocation =
        !!gpsError && (!hasPosition || errorMs > locationMs + 5000);

      if (hasPosition) {
        const accuracy = Math.round(gpsLocation!.accuracy);
        const fixTime = gpsLocation!.timestamp?.toDate?.();
        let timeAgo = '';
        if (fixTime) {
          const diffMs = Date.now() - fixTime.getTime();
          const seconds = Math.floor(diffMs / 1000);
          if (seconds < 60) timeAgo = `${seconds}s ago`;
          else if (seconds < 3600) timeAgo = `${Math.floor(seconds / 60)}m ago`;
          else if (seconds < 86400) timeAgo = `${Math.floor(seconds / 3600)}h ago`;
          else timeAgo = `${Math.floor(seconds / 86400)}d ago`;
        }
        const details = [accuracy ? `${accuracy}m accuracy` : null, timeAgo || null].filter(Boolean).join(' · ');

        // Low-accuracy fix: still a position, but flag it as a warning so the
        // user knows the precision is degraded.
        if (isLowAccuracyFix) {
          return {
            isIssue: true,
            icon: 'location_searching',
            tagText: 'Low accuracy',
            description: `Position available but accuracy is low (${accuracy}m). Could not get a more precise fix in time.`,
            severityOverride: 'potential',
          };
        }

        // A more recent error means the latest retry failed; we still show the
        // previous fix but mention the failure.
        if (errorIsNewerThanLocation && gpsError) {
          return {
            isIssue: true,
            icon: 'location_on',
            tagText: 'Last retry failed',
            description: `Showing previous fix (${details || 'unknown'}). Last GPS request failed: ${getGpsErrorMessage(gpsError.code)}.`,
            severityOverride: 'potential',
          };
        }

        return {
          isIssue: false,
          icon: 'location_on',
          tagText: 'Position',
          description: details || 'GPS position available.',
        };
      }

      // No position — distinguish "request failed" from "never requested".
      if (gpsError) {
        return {
          isIssue: isOnline === true,
          icon: 'location_off',
          tagText: getGpsErrorTagText(gpsError.code),
          description: getGpsErrorMessage(gpsError.code),
          severityOverride: 'potential',
        };
      }

      return {
        isIssue: isOnline === true,
        icon: 'location_off',
        tagText: 'No position',
        description: 'No GPS position available. Request position from the map page.',
        severityOverride: 'potential',
      };
    },
  },
  {
    id: 'dslrBattery',
    priority: 7,
    label: 'Camera Battery',
    severity: 'potential',
    getStatus: ({ device, cameraMode }) => {
      // Only show when DSLR is connected in DSLR mode
      if (cameraMode !== 'dslr' || !device?.dslr?.connected) {
        return { isIssue: false, icon: 'battery_android_frame_full', tagText: 'N/A', description: '' };
      }

      const params = device.dslr.cameraParameters;
      const batteryPercent = params?.batteryPercent;
      const isOnAcPower = params?.onAcPower ?? false;

      // Issue if battery ≤ 33% and not on AC power
      const isLowBattery = batteryPercent !== null && batteryPercent !== undefined && batteryPercent <= 33 && !isOnAcPower;

      return {
        isIssue: isLowBattery,
        icon: getBatteryIcon(batteryPercent),
        tagText: batteryPercent !== null && batteryPercent !== undefined ? `${batteryPercent}%` : 'Battery unknown',
        description: isLowBattery
          ? `Camera battery is low at ${batteryPercent}%. Replace or recharge the battery, or connect AC power.`
          : batteryPercent !== null && batteryPercent !== undefined
            ? `Camera battery at ${batteryPercent}%.`
            : 'Camera battery level is unknown.',
      };
    },
  },
  {
    id: 'dslrAcPower',
    priority: 8,
    label: 'Camera Power',
    severity: 'potential',
    getStatus: ({ device, cameraMode }) => {
      if (cameraMode !== 'dslr' || !device?.dslr?.connected) {
        return { isIssue: false, icon: 'power', tagText: 'N/A', description: '' };
      }

      const params = device.dslr.cameraParameters;
      const isOnAcPower = params?.onAcPower;

      if (isOnAcPower === null || isOnAcPower === undefined) {
        return {
          isIssue: false,
          icon: 'power',
          tagText: 'Power unknown',
          description: 'Camera power source is unknown.',
        };
      }

      if (isOnAcPower) {
        return {
          isIssue: false,
          icon: 'power',
          tagText: 'AC Power',
          description: 'Camera is powered by external AC adapter for reliable long-term operation.',
        };
      }

      const batteryPercent = params?.batteryPercent;
      const likelyDummyBattery = batteryPercent !== null && batteryPercent !== undefined && batteryPercent === 100;

      if (likelyDummyBattery) {
        return {
          isIssue: false,
          icon: 'power',
          tagText: 'Dummy battery (likely)',
          description: 'Battery at 100% — likely powered by an AC coupler (dummy battery). No action needed.',
        };
      }

      const levelInfo = batteryPercent !== null && batteryPercent !== undefined ? ` at ${batteryPercent}%` : '';
      return {
        isIssue: true,
        icon: 'battery_alert',
        tagText: 'Battery power',
        description: `Running on internal battery${levelInfo}. For long timelapses, use an AC power adapter.`,
      };
    },
  },
  {
    id: 'dslrStorage',
    priority: 9,
    label: 'Camera Storage',
    severity: 'potential',
    getStatus: ({ device, cameraMode }) => {
      // Only show when DSLR is connected in DSLR mode
      if (cameraMode !== 'dslr' || !device?.dslr?.connected) {
        return { isIssue: false, icon: 'sd_card', tagText: 'N/A', description: '' };
      }

      const params = device.dslr.cameraParameters;
      const availableShots = params?.availableShots;

      // Unknown storage - don't flag as issue
      if (availableShots === null || availableShots === undefined) {
        return {
          isIssue: false,
          icon: 'sd_card',
          tagText: 'Storage unknown',
          description: 'Camera available shots is unknown.',
        };
      }

      const isLowStorage = availableShots < 20;

      return {
        isIssue: isLowStorage,
        icon: isLowStorage ? 'sd_card_alert' : 'sd_card',
        tagText: isLowStorage ? 'SD storage low' : 'SD storage ok',
        description: isLowStorage
          ? `Only ${availableShots} shots remaining on camera SD card. Free up space or insert a larger card.`
          : `Camera has ${availableShots} shots available on SD card. This is more than enough for normal operation because photos are transferred to the controller and deleted from the card after each capture, so this space is continuously recycled.`,
      };
    },
  },
];

/**
 * Get evaluated statuses for all mission critical items
 */
export function evaluateMissionCriticalItems(
  androidStatus: AndroidStatus | null | undefined,
  cameraSettings?: CameraConfig,
  gpsLocation?: GpsLocation,
  isOnline?: boolean,
  gpsError?: GpsError,
): Array<MissionCriticalItem & { status: MissionCriticalItemStatus }> {
  const { device, capturing } = androidStatus || {};
  const cameraMode = cameraSettings?.mode ?? 'internal';
  const context: MissionCriticalContext = { device, capturing, cameraMode, gpsLocation, gpsError, isOnline };

  return missionCriticalItems.map((item) => ({
    ...item,
    status: item.getStatus(context),
  }));
}

/**
 * Get summary of mission critical status (for title)
 */
export function getMissionCriticalSummary(
  androidStatus: AndroidStatus | null | undefined,
  cameraSettings?: CameraConfig,
  gpsLocation?: GpsLocation,
  isOnline?: boolean,
  gpsError?: GpsError,
): {
  criticalCount: number;
  warningCount: number;
  title: string;
  icon: string;
  color: string;
} {
  const evaluatedItems = evaluateMissionCriticalItems(androidStatus, cameraSettings, gpsLocation, isOnline, gpsError);

  let criticalCount = 0;
  let warningCount = 0;

  evaluatedItems.forEach((item) => {
    if (item.status.isIssue) {
      // Use severityOverride if present, otherwise fall back to item severity
      const effectiveSeverity = item.status.severityOverride ?? item.severity;
      if (effectiveSeverity === 'critical') {
        criticalCount++;
      } else {
        warningCount++;
      }
    }
  });

  // Build title
  let title: string;
  let icon: string;
  let color: string;

  if (criticalCount > 0 && warningCount > 0) {
    title = `${criticalCount} critical, ${warningCount} warning`;
    icon = 'dangerous';
    color = '#ff4d4f';
  } else if (criticalCount > 0) {
    const label = criticalCount === 1 ? 'critical issue' : 'critical issues';
    title = `${criticalCount} ${label}`;
    icon = 'dangerous';
    color = '#ff4d4f';
  } else if (warningCount > 0) {
    const label = warningCount === 1 ? 'potential issue' : 'potential issues';
    title = `${warningCount} ${label}`;
    icon = 'warning';
    color = '#faad14';
  } else {
    title = 'Everything is ok';
    icon = 'check_circle';
    color = '#52c41a';
  }

  return { criticalCount, warningCount, title, icon, color };
}
